import { useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import {
  MAX_USERNAME_LENGTH,
  PARTY_MAX,
  type FriendEntry,
  type FriendRef,
  type PartyMemberWire,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import type { FriendsState, PartyState, SocialError } from "../store.js";
import { BAD, GOOD, RADIUS, SHADOW_MODAL, TEXT_DIM } from "../theme.js";
import {
  canInvite,
  inputProblem,
  isInParty,
  isPartyLeader,
  memberPhaseLabel,
  phaseLabel,
  reasonLabel,
  sortFriends,
  submittable,
} from "./friendsLogic.js";
import { IconFriends } from "./icons.js";
import { Card, CardTitle, EmptyState, Eyebrow, Note } from "./kit.js";

/**
 * Who you know, and who you are going in with. The one tab that draws NOTHING
 * of its own: every button sends a verb and waits; the state arrives whole
 * (`friends`, `partyState`). What the client can decide it decides in
 * `friendsLogic.ts`. "Which of these is me" is asked by USERNAME (`me` in the
 * store), never display name — two Sepps would get each other's Kick buttons.
 */

export interface FriendsTabProps {
  /** THE friends state, as pushed. `null` before the first one arrives. */
  state: FriendsState | null;
  /** THE party, or `null` when we are in none. */
  party: PartyState | null;
  /** Our own account handle. `null` on a session with no account. */
  me: string | null;
  /** The last refusal the server sent for a social verb. */
  error: SocialError | null;
}

/** Cards keep their height; the column scrolls, they do not squash. */
const CARD: CSSProperties = { flex: "0 0 auto" };

/** A list inside a card: rows sit closer together than the card's own gap. */
const LIST: CSSProperties = { display: "flex", flexDirection: "column", gap: 2 };

export function FriendsTab(props: FriendsTabProps): ReactElement {
  const { state, party, me } = props;
  const leader = isPartyLeader(party, me);
  const meInParty = isInParty(party, me);
  // ONE error line for the whole tab, under the field: the refusals come from
  // seven buttons but only one at a time, and a sentence that moved around
  // would
  // have to be hunted.
  const why = props.error === null ? null : reasonLabel(props.error.reason);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        width: "100%",
        maxWidth: 720,
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      {party === null ? null : (
        <PartyCard party={party} me={me} leader={leader} />
      )}

      <AddFriendCard problem={why} />

      {state === null ? (
        <Note>Waiting for the server…</Note>
      ) : (
        <>
          <RequestsCard
            heading="ASKING TO BE FRIENDS"
            refs={state.incoming}
            render={(ref) => (
              <>
                <button
                  type="button"
                  className="metabtn metabtn-sm"
                  onClick={() => uiActions().friendRespond(ref.username, true)}
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="metabtn metabtn-sm"
                  onClick={() => uiActions().friendRespond(ref.username, false)}
                >
                  Decline
                </button>
              </>
            )}
          />
          <RequestsCard
            heading="WAITING ON AN ANSWER"
            refs={state.outgoing}
            render={(ref) => (
              <button
                type="button"
                className="metabtn metabtn-sm"
                onClick={() => uiActions().friendRemove(ref.username)}
              >
                Withdraw
              </button>
            )}
          />
          <FriendsCard
            friends={state.friends}
            party={party}
            meInParty={meInParty}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The squad, in JOIN ORDER — also the order leadership passes in, so a member
 * leaving moves the star up and nothing else.
 */
function PartyCard(props: {
  party: PartyState;
  me: string | null;
  leader: boolean;
}): ReactElement {
  const { party, me } = props;
  return (
    <Card style={CARD}>
      <CardTitle>
        YOUR SQUAD · {party.members.length}/{PARTY_MAX}
      </CardTitle>
      <div style={LIST}>
        {party.members.map((member) => (
          <MemberRow
            key={member.username}
            member={member}
            // The leader may throw anyone out but themselves (leaving is the
            // button
            // below). A session that cannot say who it is offers no kicks — here
            // the
            // safe answer is to withhold.
            canKick={props.leader && me !== null && member.username !== me}
          />
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className="metabtn"
          onClick={() => uiActions().partyLeave()}
        >
          Leave squad
        </button>
      </div>
      <Note>
        {props.leader
          ? "You queue for the squad — everyone deploys together."
          : "The squad leader queues; you deploy with them."}
      </Note>
    </Card>
  );
}

function MemberRow(props: { member: PartyMemberWire; canKick: boolean }): ReactElement {
  const { member } = props;
  return (
    <PersonRow
      online={member.online}
      name={`${member.leader ? "★ " : ""}${member.name}`}
      username={member.username}
      status={memberPhaseLabel(member)}
    >
      {props.canKick ? (
        <button
          type="button"
          className="metabtn metabtn-sm"
          onClick={() => uiActions().partyKick(member.username)}
        >
          Kick
        </button>
      ) : null}
    </PersonRow>
  );
}

// ---------------------------------------------------------------------------

/**
 * The field, its one button, and whatever the server last said no to. The
 * field's own complaint takes precedence — it is about the box right now.
 */
function AddFriendCard(props: { problem: string | null }): ReactElement {
  const [raw, setRaw] = useState("");
  const typed = inputProblem(raw);
  const name = submittable(raw);

  const submit = (): void => {
    if (name === null) return;
    uiActions().friendRequest(name);
    setRaw("");
  };

  return (
    <Card style={CARD}>
      <CardTitle>ADD A FRIEND</CardTitle>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          className="metainput"
          type="text"
          value={raw}
          maxLength={MAX_USERNAME_LENGTH}
          placeholder="username"
          spellCheck={false}
          autoComplete="off"
          aria-label="Username"
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
        <button type="button" className="metabtn" disabled={name === null} onClick={submit}>
          Send request
        </button>
      </div>
      {typed === null ? null : <Note>{typed}</Note>}
      {typed === null && props.problem !== null ? (
        <Note style={{ color: BAD }}>{props.problem}</Note>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------

/**
 * One of the two request lists — ABSENT ENTIRELY when empty, since two
 * permanent boxes explaining nothing would push the friends list off the
 * screen.
 */
function RequestsCard(props: {
  heading: string;
  refs: readonly FriendRef[];
  render: (ref: FriendRef) => ReactNode;
}): ReactElement | null {
  if (props.refs.length === 0) return null;
  return (
    <Card style={CARD}>
      <CardTitle aside={props.refs.length}>{props.heading}</CardTitle>
      <div style={LIST}>
        {props.refs.map((ref) => (
          // A pending request carries no presence — whether a stranger is online
          // is not yours to see until they say yes — so the dot stays dark.
          <PersonRow key={ref.username} online={false} name={ref.name} username={ref.username}>
            {props.render(ref)}
          </PersonRow>
        ))}
      </div>
    </Card>
  );
}

function FriendsCard(props: {
  friends: readonly FriendEntry[];
  party: PartyState | null;
  meInParty: boolean;
}): ReactElement {
  const sorted = sortFriends(props.friends);
  return (
    <Card style={CARD}>
      <CardTitle aside={props.friends.length}>FRIENDS</CardTitle>
      {sorted.length === 0 ? (
        <EmptyState icon={<IconFriends />} title="Nobody yet.">
          Add someone by their username above.
        </EmptyState>
      ) : (
        <div style={LIST}>
          {sorted.map((friend) => (
            <FriendRow
              key={friend.username}
              friend={friend}
              party={props.party}
              meInParty={props.meInParty}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function FriendRow(props: {
  friend: FriendEntry;
  party: PartyState | null;
  meInParty: boolean;
}): ReactElement {
  const { friend } = props;
  const verdict = canInvite(friend, props.party, props.meInParty);
  return (
    <PersonRow
      online={friend.online}
      name={friend.name}
      username={friend.username}
      status={phaseLabel(friend)}
    >
      <button
        type="button"
        className="metabtn metabtn-sm"
        disabled={!verdict.ok}
        // The reason is the title rather than a line of its own — one per
        // friend
        // would drown the list. The tab's error line covers a refused click.
        title={verdict.ok ? "Invite to squad" : (verdict.why ?? "")}
        onClick={() => uiActions().partyInvite(friend.username)}
      >
        Invite to squad
      </button>
      <button
        type="button"
        className="metabtn metabtn-sm"
        onClick={() => uiActions().friendRemove(friend.username)}
      >
        Remove
      </button>
    </PersonRow>
  );
}

// ---------------------------------------------------------------------------

/**
 * The invite prompt, over whatever tab is up. Dismissing IS declining: an
 * unanswered invite would be met again the moment the tab opened. No third
 * button.
 */
export function PartyInvitePrompt(props: { from: FriendRef }): ReactElement {
  return (
    <section
      style={{
        position: "absolute",
        top: 14,
        right: 18,
        // One above the queue modal (65): an invite that arrived while waiting
        // is
        // exactly the one to answer.
        zIndex: 66,
        width: 320,
        padding: "14px 16px",
        boxSizing: "border-box",
        borderRadius: RADIUS,
        background: "rgb(19, 22, 26)",
        border: "1px solid rgba(214, 176, 108, 0.45)",
        boxShadow: SHADOW_MODAL,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        pointerEvents: "auto",
      }}
    >
      <Eyebrow>SQUAD INVITE</Eyebrow>
      <span style={{ lineHeight: 1.45 }}>{props.from.name} invites you to their squad</span>
      <Note>{props.from.username}</Note>
      <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
        <button
          type="button"
          className="metabtn"
          onClick={() => uiActions().partyRespond(true)}
        >
          Accept
        </button>
        <button
          type="button"
          className="metabtn"
          onClick={() => uiActions().partyRespond(false)}
        >
          Decline
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

/** One person, however they got onto the screen: dot, name, handle, buttons. */
function PersonRow(props: {
  online: boolean;
  name: string;
  username: string;
  status?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "5px 0",
        fontSize: 12.5,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flex: "0 0 auto",
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: props.online ? GOOD : TEXT_DIM,
        }}
      />
      <span style={{ flex: 1, minWidth: 0, fontWeight: 500, overflowWrap: "anywhere" }}>
        {props.name}
        <Note style={{ marginLeft: 8 }}>{props.username}</Note>
      </span>
      {props.status === undefined ? null : (
        <Note style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}>{props.status}</Note>
      )}
      {props.children}
    </div>
  );
}
